import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerBody,
  DrawerFooter,
  Button,
  FormControl,
  Text,
  Stack,
  Box,
  FormLabel,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Flex,
  IconButton,
  Tag,
  VStack,
  HStack,
  TagLabel,
  TagCloseButton,
  Textarea,
  Badge,
  Icon,
  Collapse,
  Center,
  Spinner
} from "@chakra-ui/react";
import { ChevronDownIcon, CloseIcon } from "@chakra-ui/icons";
import { FilterProto, Header, MetricProto, ObservationColumnProto } from "build-ts/metadata";
import { KGObservationProto, ObservationEditType, RecommendationRuleProto } from "build-ts/analysis_plan";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";
import { ColumnHashProto } from "build-ts/data";
import { ColumnType, MetadataTypeEnum, TimeBucket } from "build-ts/enums";
import debounce from "lodash.debounce";
import { formatToSentenceCase } from "utils/nameUtils";
import showToast from "hooks/useCustomToast";
import { getKGColumnValues } from "services/DataModelService";
import ColumnValues from "components/common/ColumnValues";
import { getNodeData } from "services/AnalysisTreeServices";
import { AnalysisTreeNodeProto } from "build-ts/analysis";
import { buildKGAnalysisPlanProto, useAnalysisPlanContext } from "context/batch/AnalysisPlanContext";
import Filters from "./Filters";
import Benchmarks from "./Benchmarks";
import Analysis from "./Analysis";
import { StepInterface, PropagationItem, AvailableNode } from "./Interface";
import PropagationModal from "containers/analysis-plan/PropagationModal";
import { MdBarChart, MdOutlineQueryStats } from "react-icons/md";
import { FaFilter } from "react-icons/fa6";
import { AiFillPieChart } from "react-icons/ai";
import { FaInfoCircle, FaMagic, FaRegWindowMinimize } from "react-icons/fa";
import { TbLayoutSidebarRightCollapseFilled, TbMathFunction, TbFilterBolt } from "react-icons/tb";
import { PlanInput } from "build-ts/agent";
import { generateNodeLabel } from "services/AnalysisPlanServices";
import { ChatMessage_Author, chatMessage_AuthorFromJSON } from "build-ts/chat";

const NodeConfigurator: React.FC = () => {
  const {
    selectedNodeId,
    setSelectedNodeId,
    configurator,
    nodeMap,
    handleNodeSave,
    handleGetDataForNode,
    setObservationData,
    showRecommendedFilters,
    setShowRecommendedFilters,
    updateNode,
    plan,
    selectedMetricHeader,
    currentPlanHeader,
  } = useAnalysisPlanContext();

  const [nodeName, setNodeName] = useState<string>("");
  const [currentNode, setCurrentNode] = useState<StepInterface | null>(null);
  const [selectedColumns, setSelectedColumns] = useState<ObservationColumnProto[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<MetricProto[]>([]);
  const [filters, setFilters] = useState<FilterProto[]>([]);
  const [selectedBenchmarks, setSelectedBenchmarks] = useState<BenchmarkEntityProto[]>([]);
  const [analysisColumns, setAnalysisColumns] = useState<ColumnHashProto[]>([]);
  const [benchmarkColumnHashes, setBenchmarkColumnHashes] = useState<ColumnHashProto[]>([]);
  const [recommendationRules, setRecommendationRules] = useState<RecommendationRuleProto[]>([]);
  // State for propagation modal
  const [propagationModal, setPropagationModal] = useState<{
    isOpen: boolean;
    item: PropagationItem | null;
    availableNodes: AvailableNode[];
    shouldOverwrite: boolean;
  }>({
    isOpen: false,
    item: null,
    availableNodes: [],
    shouldOverwrite: true
  });

  // State for column dropdown selection
  const [selectedColumn, setSelectedColumn] = useState<Header | null>(null);
  // State for metric dropdown selection  
  const [selectedMetric, setSelectedMetric] = useState<MetricProto | null>(null);

  // Flag to track if we're initializing (loading node data) vs user making changes
  const [isInitializing, setIsInitializing] = useState(false);

  // Flag to prevent auto-save during save operations
  const [isSaving, setIsSaving] = useState(false);

  // Loading state for Get Data operation
  const [isGettingData, setIsGettingData] = useState<boolean>(false);

  // State for recommended filters when Get Data is clicked
  const [recommendedFiltersToApply, setRecommendedFiltersToApply] = useState<FilterProto[]>([]);
  const [recommendedFilterValues, setRecommendedFilterValues] = useState<Map<string, string[]>>(new Map());
  const [selectedRecommendedFilters, setSelectedRecommendedFilters] = useState<Map<string, string[]>>(new Map());
  const [isLoadingFilterValues, setIsLoadingFilterValues] = useState<boolean>(false);

  // State for generating node label
  const [isGeneratingNodeLabel, setIsGeneratingNodeLabel] = useState<boolean>(false);
  const [newGeneratedNodeLabel, setNewGeneratedNodeLabel] = useState<string>("");

  const [isMetricsExpanded, setIsMetricsExpanded] = useState<boolean>(false);
  const toggleMetricsExpansion = () => {
    setIsMetricsExpanded(!isMetricsExpanded);
    setIsColumnsExpanded(false);
    setIsBenchmarksExpanded(false);
    setIsFiltersExpanded(false);
    setIsTableInfoExpanded(false);
    setIsAnalysisExpanded(false);
  };

  const [isColumnsExpanded, setIsColumnsExpanded] = useState<boolean>(false);
  
  const toggleColumnsExpansion = () => {
    setIsColumnsExpanded(!isColumnsExpanded);
    setIsMetricsExpanded(false);
    setIsBenchmarksExpanded(false);
    setIsFiltersExpanded(false);
    setIsTableInfoExpanded(false);
    setIsAnalysisExpanded(false);
  };
  const [isBenchmarksExpanded, setIsBenchmarksExpanded] = useState<boolean>(false);
  const toggleBenchmarksExpansion = () => {
    setIsBenchmarksExpanded(!isBenchmarksExpanded);
    setIsMetricsExpanded(false);
    setIsColumnsExpanded(false);
    setIsFiltersExpanded(false);
    setIsTableInfoExpanded(false);
    setIsAnalysisExpanded(false);
  };

  const [isFiltersExpanded, setIsFiltersExpanded] = useState<boolean>(false);
  const toggleFiltersExpansion = () => {
    setIsFiltersExpanded(!isFiltersExpanded);
    setIsMetricsExpanded(false);
    setIsColumnsExpanded(false);
    setIsBenchmarksExpanded(false);
    setIsTableInfoExpanded(false);
    setIsAnalysisExpanded(false);
  };
  
  const [isTableInfoExpanded, setIsTableInfoExpanded] = useState<boolean>(false);
  const toggleTableInfoExpansion = () => {
    setIsTableInfoExpanded(!isTableInfoExpanded);
    setIsMetricsExpanded(false);
    setIsColumnsExpanded(false);
    setIsBenchmarksExpanded(false);
    setIsFiltersExpanded(false);
    setIsAnalysisExpanded(false);
  };

  const [isAnalysisExpanded, setIsAnalysisExpanded] = useState<boolean>(false);
  const toggleAnalysisExpansion = () => {
    setIsAnalysisExpanded(!isAnalysisExpanded);
    setIsMetricsExpanded(false);
    setIsColumnsExpanded(false);
    setIsBenchmarksExpanded(false);
    setIsFiltersExpanded(false);
    setIsTableInfoExpanded(false);
  };

  // Refs to track previous values for change detection
  const previousValuesRef = useRef({
    nodeName: "",
    selectedColumns: [] as ObservationColumnProto[],
    selectedMetrics: [] as MetricProto[],
    filters: [] as FilterProto[],
    selectedBenchmarks: [] as BenchmarkEntityProto[],
    analysisColumns: [] as ColumnHashProto[],
    benchmarkColumnHashes: [] as ColumnHashProto[],
    recommendationRules: [] as RecommendationRuleProto[],
  });

  // Get available metrics from currentNode that aren't already selected
  const availableMetrics = useMemo(() => {
    if (!currentNode?.metrics) return [];

    // Filter out metrics that are already selected
    const selectedMetricIds = new Set(selectedMetrics.map(m => m.header?.id));
    return currentNode.metrics.filter(m => !selectedMetricIds.has(m.header?.id));
  }, [currentNode?.metrics, selectedMetrics]);

  // Get non-metric columns for the column dropdown (exclude metrics)
  const nonMetricColumns = useMemo(() => {
    if (!currentNode?.non_benchmarks) return [];

    return currentNode.non_benchmarks.filter(col => 
      col.kg_column_header?.type !== MetadataTypeEnum.METRIC
    );
  }, [currentNode?.non_benchmarks]);

  // Extract recommended columns from recommendation_edits
  const recommendedColumns = useMemo(() => {
    if (!currentNode?.recommendation_edits) return [];

    const columnEdits: ObservationColumnProto[] = [];

    // Process recommendation edits
    currentNode?.recommendation_edits?.forEach(edit => {
      // Process select_anyone field
      if (edit?.select_anyone) {
        edit.select_anyone.forEach(columnId => {
          // Only add non-metric columns to recommendedColumns
          const col = nonMetricColumns.find(col => col.column_id === columnId);
          if (col) {
            columnEdits.push(col);
          }
        });
      }

      // Process edits field for backward compatibility
      if (edit?.edits) {
        edit?.edits?.forEach(observationEdit => {
          if (observationEdit.edit_type === ObservationEditType.ADD_COLUMN 
            && observationEdit.column
            && observationEdit.column.kg_column_header?.type !== MetadataTypeEnum.METRIC) {
            columnEdits.push(observationEdit.column);
          }
        });
      }
    });

    return columnEdits;
  }, [currentNode?.recommendation_edits, nonMetricColumns]);

  // Extract recommended metrics from recommendation_edits  
  const recommendedMetrics = useMemo(() => {
    if (!currentNode?.recommendation_edits) return [];

    const metricEdits: MetricProto[] = [];

    // Process recommendation edits
    currentNode?.recommendation_edits?.forEach(edit => {
      // Process select_anyone field for metrics
      if (edit?.select_anyone) {
        edit.select_anyone.forEach(columnId => {
          const metric = currentNode.metrics?.find(metric => metric.header?.id === columnId);
          if (metric) {
            metricEdits.push(metric);
          }
        });
      }

      // Process edits field for backward compatibility
      if (edit?.edits) {
        edit?.edits?.forEach(observationEdit => {
          if (observationEdit.edit_type === ObservationEditType.ADD_METRIC 
            && observationEdit.metric) {
            metricEdits.push(observationEdit.metric);
          }
        });
      }
    });

    return metricEdits;
  }, [currentNode?.recommendation_edits, currentNode?.metrics]);

  // Extract recommended filters from recommendation_edits
  const recommendedFilters = useMemo(() => {
    if (!currentNode?.recommendation_edits) return [];

    const filterEdits: FilterProto[] = [];

    // Extract filters from ADD_FILTER edits
    currentNode?.recommendation_edits?.forEach(edit => {
      if (edit?.edits) {
        edit?.edits?.forEach(observationEdit => {
          if (observationEdit.edit_type === ObservationEditType.ADD_FILTER 
            && observationEdit.filter) {
            filterEdits.push(observationEdit.filter);
          }
        });
      }
    });

    return filterEdits;
  }, [currentNode?.recommendation_edits]);

  // Initialize state when selectedNodeId changes
  useEffect(() => {
    if (selectedNodeId) {
      const node = nodeMap?.get(selectedNodeId);
      if (node) {
        setIsInitializing(true);

        setCurrentNode(node);
        setNodeName(node.label);
        setSelectedColumns(node.selected_columns || []);
        setSelectedMetrics(node.selected_metrics || []);
        setSelectedBenchmarks(node.selected_benchmarks || []);
        setAnalysisColumns(node.analysis_columns || []);
        setBenchmarkColumnHashes(node.benchmark_column_hashes || []);
        setFilters(node.selected_filters || []);
        setRecommendationRules(node.recommendation_rules || []);
        setNewGeneratedNodeLabel("");

        // Update previous values ref to current values to prevent auto-save trigger
        previousValuesRef.current = {
          nodeName: node.label,
          selectedColumns: [...(node.selected_columns || [])],
          selectedMetrics: [...(node.selected_metrics || [])],
          filters: [...(node.selected_filters || [])],
          selectedBenchmarks: [...(node.selected_benchmarks || [])],
          analysisColumns: [...(node.analysis_columns || [])],
          benchmarkColumnHashes: [...(node.benchmark_column_hashes || [])],
          recommendationRules: [...(node.recommendation_rules || [])],
        };

        // Mark initialization as complete
        setIsInitializing(false);
      } else {
        setCurrentNode(null);
      }
    }
  }, [selectedNodeId, nodeMap]);

  // Function to detect if any values have actually changed
  const hasActualChanges = useCallback((): boolean => {
    if (isInitializing || !currentNode) return false;

    const current = {
      nodeName,
      selectedColumns,
      selectedMetrics,
      filters,
      selectedBenchmarks,
      analysisColumns,
      benchmarkColumnHashes,
      recommendationRules,
    };

    const previous = previousValuesRef.current;

    // Deep comparison for each field
    const nodeNameChanged = current.nodeName !== previous.nodeName;

    const columnsChanged = current.selectedColumns.length !== previous.selectedColumns.length ||
      current.selectedColumns.some((col, idx) => 
        col.column_id !== previous.selectedColumns[idx]?.column_id ||
        col.time_bucket !== previous.selectedColumns[idx]?.time_bucket);

    const metricsChanged = current.selectedMetrics.length !== previous.selectedMetrics.length ||
      current.selectedMetrics.some((metric, idx) => 
        metric.header?.id !== previous.selectedMetrics[idx]?.header?.id);

    const filtersChanged = current.filters.length !== previous.filters.length ||
      current.filters.some((filter, idx) => 
        filter.filter_id !== previous.filters[idx]?.filter_id);

    const benchmarksChanged = current.selectedBenchmarks.length !== previous.selectedBenchmarks.length ||
      current.selectedBenchmarks.some((benchmark, idx) => 
        benchmark.header?.id !== previous.selectedBenchmarks[idx]?.header?.id);

    const analysisColumnsChanged = current.analysisColumns.length !== previous.analysisColumns.length ||
      current.analysisColumns.some((col, idx) => 
        col.actuals_id !== previous.analysisColumns[idx]?.actuals_id ||
        col.benchmark_id !== previous.analysisColumns[idx]?.benchmark_id ||
        col.analysis_column_type !== previous.analysisColumns[idx]?.analysis_column_type ||
        col.column_name !== previous.analysisColumns[idx]?.column_name);

    const benchmarkColumnHashesChanged = current.benchmarkColumnHashes.length !== previous.benchmarkColumnHashes.length ||
      current.benchmarkColumnHashes.some((col, idx) => 
        col.actuals_id !== previous.benchmarkColumnHashes[idx]?.actuals_id ||
        col.benchmark_id !== previous.benchmarkColumnHashes[idx]?.benchmark_id  ||
        col.column_name !== previous.benchmarkColumnHashes[idx]?.column_name);

    const recommendationRulesChanged = current.recommendationRules.length !== previous.recommendationRules.length;

    const hasChanges = nodeNameChanged || columnsChanged || metricsChanged || filtersChanged || 
                      benchmarksChanged || analysisColumnsChanged || benchmarkColumnHashesChanged || recommendationRulesChanged;

    return hasChanges;
  }, [nodeName, selectedColumns, selectedMetrics, filters, selectedBenchmarks, analysisColumns, benchmarkColumnHashes, recommendationRules, isInitializing, currentNode]);

  // Debounced auto-save function
  const debouncedAutoSave = useCallback(
    debounce(() => {
      if (hasActualChanges() && !isSaving && currentNode) {        
        setIsSaving(true);

        const nodeInfo = {
          nodeName,
          selectedColumns,
          selectedBenchmarks,
          filters,
          analysisColumns,
          benchmarkColumnHashes,
          recommendationRules,
          selectedMetrics
        };

        handleNodeSave(currentNode?.id, nodeInfo);

        // Update previous values after successful save
        previousValuesRef.current = {
          nodeName,
          selectedColumns: [...selectedColumns],
          selectedMetrics: [...selectedMetrics],
          filters: [...filters],
          selectedBenchmarks: [...selectedBenchmarks],
          analysisColumns: [...analysisColumns],
          benchmarkColumnHashes: [...benchmarkColumnHashes],
          recommendationRules: [...recommendationRules],
        };

        setIsSaving(false);
      }
    }, 500), // 500ms delay for auto-save
    [isSaving, currentNode, nodeName, selectedColumns, selectedMetrics, filters, selectedBenchmarks, analysisColumns, benchmarkColumnHashes, recommendationRules]
  );

  // Single effect to trigger auto-save when any relevant state changes
  useEffect(() => {
    if (!isInitializing && !isSaving && currentNode) {
      debouncedAutoSave();
    }
  }, [nodeName, selectedColumns, selectedMetrics, filters, selectedBenchmarks, analysisColumns, benchmarkColumnHashes, recommendationRules, debouncedAutoSave, isInitializing, isSaving, currentNode]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedAutoSave.cancel();
    };
  }, [debouncedAutoSave]);

  // Check if a column is in the recommended columns list
  const isRecommendedColumn = (columnId: string): boolean => {
    return recommendedColumns.some(col => 
      col.column_id === columnId || 
      col.kg_column_header?.id === columnId);
  };

  // Check if a metric is in the recommended metrics list
  const isRecommendedMetric = (metricId: string): boolean => {
    return recommendedMetrics.some(metric => 
      metric.header?.id === metricId);
  };


  // Generic handleSelectColumn that works for all column types
  const handleSelectColumn = (column: ObservationColumnProto) => {
    if (selectedColumns.some(c => c.column_id === column.column_id)) return;

    // For temporal columns, add with default time bucket (can be edited later)
    if (column.kg_column_header?.column_type === ColumnType.TEMPORAL) {
      setSelectedColumns(prev => [...prev, { ...column, time_bucket: TimeBucket.MONTHLY }]);
    } else {
      setSelectedColumns(prev => [...prev, column]);
    }
    setSelectedColumn(null);
  };

  // Handle metric selection from dropdown
  const handleSelectMetric = (metric: MetricProto) => {
    setSelectedMetric(metric);
    // Check if metric is already selected
    if (!selectedMetrics.some(m => m.header?.id === metric.header?.id)) {
      setSelectedMetrics(prev => [...prev, metric]);
    }
    // Reset selected metric to allow further selections
    setSelectedMetric(null);
  };

  // Remove a column from selection
  const handleRemoveColumn = (columnId: string) => {
    setSelectedColumns(prev => prev.filter(column => column.column_id !== columnId));
  };

  // Remove a metric from selection
  const handleRemoveMetric = (metricId: string) => {
    setSelectedMetrics(prev => prev.filter(metric => metric.header?.id !== metricId));
  };

  // Handle time bucket change for temporal columns
  const handleTimeBucketChange = (columnId: string, timeBucket: TimeBucket) => {
    setSelectedColumns(prev => 
      prev.map(column => 
        column?.column_id === columnId 
          ? { ...column, time_bucket: timeBucket }
          : column
      )
    );
  };

  // Handle metrics propagation click
  const handlePropagateMetricsClick = (currentNodeId: string) => {
    // Get all nodes from the nodeMap, excluding the current node
    const allNodeIds = Array.from(nodeMap.keys()).filter(id => id !== currentNodeId);
    const allNodes = allNodeIds.map((id) => {
      const node = nodeMap.get(id);
      return {
        id,
        name: node?.label || `Node ${id}`,
        selected: true,
      };
    });
    
    setPropagationModal({
      isOpen: true,
      item: {
        type: "metrics",
        metrics: selectedMetrics
      },
      availableNodes: allNodes,
      shouldOverwrite: true
    });
  };

  // Handle propagation modal node toggle
  const handlePropagationNodeToggle = (nodeId: string) => {
    setPropagationModal(prev => ({
      ...prev,
      availableNodes: prev.availableNodes.map(node => 
        node.id === nodeId 
          ? { ...node, selected: !node.selected }
          : node
      )
    }));
  };

  // Handle propagation modal overwrite toggle
  const handlePropagationOverwriteToggle = (shouldOverwrite: boolean) => {
    setPropagationModal(prev => ({
      ...prev,
      shouldOverwrite
    }));
  };

  // Handle propagation confirmation
  const handleConfirmPropagation = () => {
    if (!currentNode || !propagationModal.item) return;

    // Only use selected descendants for propagation
    const selectedDescendantIds = propagationModal.availableNodes
      .filter(node => node.selected)
      .map(node => node.id);

    if (propagationModal.item?.type === "metrics" && propagationModal.item?.metrics) {
      selectedDescendantIds.forEach(descendantId => {
        const descendantNode = nodeMap?.get(descendantId);
        if (descendantNode && propagationModal.item?.metrics) {
          if (propagationModal.shouldOverwrite) {
            // Replace all existing metrics
            descendantNode.selected_metrics = [...propagationModal.item.metrics];
          } else {
            // Add metrics that don't already exist
            const existingMetricIds = new Set(
              descendantNode.selected_metrics?.map(m => m.header?.id) || []
            );
            const newMetrics = propagationModal.item.metrics.filter(
              metric => !existingMetricIds.has(metric.header?.id)
            );
            descendantNode.selected_metrics = [
              ...(descendantNode.selected_metrics || []),
              ...newMetrics
            ];
          }
          updateNode(descendantNode);
        }
      });
    }

    // Close modal and reset state
    setPropagationModal({
      isOpen: false,
      item: null,
      availableNodes: [],
      shouldOverwrite: true
    });
  };

  // Close propagation modal
  const handleClosePropagation = () => {
    setPropagationModal({
      isOpen: false,
      item: null,
      availableNodes: [],
      shouldOverwrite: true
    });
  };

  // Get KG observation data for the current node
  const handleGetData = async () => {
    if (!currentNode) {
      showToast("No node selected", "", "error");
      return;
    }

    // Check for recommended filters
    const recommendedFiltersFromEdits: FilterProto[] = [];
    currentNode?.recommendation_edits?.forEach(edit => {
      if (edit?.edits) {
        edit?.edits?.forEach(observationEdit => {
          if (observationEdit.edit_type === ObservationEditType.ADD_FILTER 
            && observationEdit.filter) {
            recommendedFiltersFromEdits.push(observationEdit.filter);
          }
        });
      }
    });

    // If there are recommended filters, show them for selection
    if (recommendedFiltersFromEdits.length > 0) {
      setRecommendedFiltersToApply(recommendedFiltersFromEdits);
      setShowRecommendedFilters(true);
      // Fetch values for each recommended filter
      await fetchRecommendedFilterValues(recommendedFiltersFromEdits);
      return;
    }

    // If no recommended filters, proceed with getting data
    setIsGettingData(true);
    await handleGetDataForNode(currentNode.id);
    setIsGettingData(false);
  };

  // Function to fetch values for recommended filters
  const fetchRecommendedFilterValues = async (recommendedFilters: FilterProto[]) => {
    setIsLoadingFilterValues(true);
    const newFilterValues = new Map<string, string[]>();
    const newSelectedFilters = new Map<string, string[]>();

    for (const filter of recommendedFilters) {
      const columnId = filter.kg_column_header?.id;
      if (columnId) {
        try {
          const values = await getKGColumnValues(columnId);
          newFilterValues.set(columnId, values || []);
          // Initialize with empty selection
          newSelectedFilters.set(columnId, []);
        } catch (error) {
          console.error("Error fetching values for recommended filter:", error);
          newFilterValues.set(columnId, []);
          newSelectedFilters.set(columnId, []);
        }
      }
    }

    setRecommendedFilterValues(newFilterValues);
    setSelectedRecommendedFilters(newSelectedFilters);
    setIsLoadingFilterValues(false);
  };

  // Function to apply recommended filters and get data (without updating node state)
  const handleApplyRecommendedFilters = async () => {
    if (!currentNode) return;

    // Create filters array with existing filters plus selected recommended filters
    const appliedFilters = [...(currentNode.selected_filters || [])];

    // Add selected recommended filters
    recommendedFiltersToApply.forEach(filter => {
      const columnId = filter.kg_column_header?.id;
      if (columnId && selectedRecommendedFilters.has(columnId)) {
        const selectedValues = selectedRecommendedFilters.get(columnId) || [];
        if (selectedValues.length > 0) {
          appliedFilters.push({
            ...filter,
            values: selectedValues,
          });
        }
      }
    });

    // Create KG observation with applied filters (don't update node state)
    const kgObservation = KGObservationProto.create({
      metrics: currentNode?.selected_metrics || [],
      benchmarks: currentNode?.selected_benchmarks || [],
      filters: appliedFilters,
      columns: currentNode?.selected_columns || []
    });

    // Hide the recommended filters section
    setShowRecommendedFilters(false);
    setRecommendedFiltersToApply([]);
    setRecommendedFilterValues(new Map());
    setSelectedRecommendedFilters(new Map());
    setIsGettingData(true);
    const request = AnalysisTreeNodeProto.create({
      id: currentNode.id,
      node_label: currentNode.label,
      node_depth: currentNode.node_depth,
      kg_observation: kgObservation,
      analysis_columns: currentNode?.analysis_columns || [],
    });
    // Get data with the KG observation containing applied filters
    const response = await getNodeData(request);
    if (response) {
      // Create NodeInsights structure that DataTableMKG expects
      setObservationData({current_node: response});
      configurator.onClose();
    } else {
      showToast("Failed to fetch observation data", "", "error");
    }
    setIsGettingData(false);
  };

  // Handle generate node label
  const handleGenerateNodeLabel  = async (step_id: string | undefined) => {
    if (!step_id) {
      showToast("No step id to generate node label", "", "error");
      return;
    }
    setIsGeneratingNodeLabel(true);
    if(plan){
      const currentPlanProto = buildKGAnalysisPlanProto(plan, nodeMap, selectedMetricHeader, currentPlanHeader);
      const planInput = PlanInput.create({
        plan: currentPlanProto,
        step_id: step_id,
      });

    const response = await generateNodeLabel(planInput);
    if (response) {
      if(response[0]?.messages){
        for(let i=response[0]?.messages?.length - 1; i>=0; i--){
          if(chatMessage_AuthorFromJSON(response[0]?.messages[i].author) === (ChatMessage_Author.AI)){
            // Remove surrounding double-quotes if they exist
            const content = response[0]?.messages[i].content;
            const cleanContent = content?.replace(/^["']|["']$/g, "") || "";
            setNewGeneratedNodeLabel(cleanContent);
            break;
          }
        }
      }
    }
    }
    setIsGeneratingNodeLabel(false);
  };

  return (
    <Drawer
      isOpen={configurator.isOpen}
      placement="right"
      onClose={configurator.onClose}
      size="md"
    >
      <DrawerContent>
        <DrawerBody>
          <Box
            borderWidth="2px"
            borderRadius="md"
            borderColor="purple.700"
            boxShadow="md"
            mb={4}
            _hover={isTableInfoExpanded ? { bg: "transparent" } : { bg: "purple.50" }}
          >
            {/* Header - Always Visible */}
            <HStack 
              spacing={2} 
              cursor="pointer" 
              onClick={toggleTableInfoExpansion}
              p={3}
              borderRadius="md"
              justifyContent="space-between"
            >
              <HStack spacing={2}>
                <Icon as={FaInfoCircle} boxSize={5} color="purple.700" />
                <Text fontWeight="700" fontSize="lg" mb={0} color="purple.700">
                  Table Info
                </Text>
              </HStack>
              {/* Show minimize button when expanded */}
              {isTableInfoExpanded && (
                <IconButton
                  aria-label="Minimize section"
                  icon={<Icon as={FaRegWindowMinimize} boxSize={4} color="purple.700" />}
                  size="sm"
                  variant="ghost"
                  color="purple.700"
                  _hover={{ bg: "purple.100" }}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering parent onClick
                    toggleTableInfoExpansion();
                  }}
                />
              )}
            </HStack>
              {/* Help text when collapsed */}
            {!isTableInfoExpanded && (
              <Box 
                px={3} 
                pb={3} 
                cursor="pointer" 
                onClick={toggleTableInfoExpansion}
                _hover={{ bg: "purple.50" }}
                >
                <Text fontSize="sm" color="gray.600" mb={0}>
                 Define table name and description for Anko
                </Text>
              </Box>
            )}
            {/* Collapsible Content */}
            <Collapse in={isTableInfoExpanded} animateOpacity>
              <Box p={3} pt={0}>
                <FormControl>
                  <HStack spacing={2} justifyContent="space-between" align="center">
                    <FormLabel mb={1} fontWeight="semibold" color="purple.700" ml={1}> Name</FormLabel>
                    <Button
                      colorScheme="yellow"
                      variant="outline"
                      size="xs"
                      isDisabled={isGeneratingNodeLabel}
                      isLoading={isGeneratingNodeLabel}
                      loadingText="Generating..."
                      leftIcon={!isGeneratingNodeLabel ? <Icon as={FaMagic} boxSize={3}/> : undefined}
                      onClick={() => handleGenerateNodeLabel(currentNode?.id)}
                      borderColor="yellow.500"
                      color="yellow.600"
                      _hover={{ 
                        bg: "yellow.50", 
                        borderColor: "yellow.400",
                        color: "yellow.700"
                      }}
                      _active={{
                        bg: "yellow.100"
                      }}
                      fontWeight="semibold"
                      fontSize="xs"
                      p={1}
                      m={1}
                    >
                      Generate Node Label
                    </Button>  
                  </HStack>
                  <Textarea
                    value={nodeName}
                    onChange={(e) => setNodeName(e.target.value)}
                    placeholder="Enter table name..."
                    size="sm"
                    fontWeight="600"
                    fontSize="md"
                    rows={2}
                    resize="none"
                    colorScheme="purple"
                    _focus={{
                      borderColor: "purple.700",
                      boxShadow: "0 0 0 1px purple.700"
                    }}
                  />
                  {/* Suggested Node Label */}
                  {newGeneratedNodeLabel && (
                    <VStack spacing={2} align="stretch" p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200" mt={2}>
                      <HStack justify="space-between" align="flex-start">
                        <Text fontSize="sm" fontWeight="semibold" color="blue.700" mb={1}>
                          Suggested Node Label:
                        </Text>
                        <Button
                          size="xs"
                          colorScheme="blue"
                          variant="solid"
                          onClick={() => {
                            setNodeName(newGeneratedNodeLabel);
                            setNewGeneratedNodeLabel(""); // Clear suggestion after applying
                          }}
                          isDisabled={isGeneratingNodeLabel}
                        >
                          Apply
                        </Button>
                      </HStack>
                      <Text fontSize="sm" color="gray.700" bg="white" p={2} borderRadius="sm" borderWidth="1px" borderColor="gray.200">
                        {newGeneratedNodeLabel}
                      </Text>
                    </VStack>
                  )}
                  <FormLabel mb={1} fontWeight="semibold" color="purple.700" ml={1} mt={2}> Description</FormLabel>
                  <Textarea
                    placeholder="Table Description for Agent..."
                    size="sm"
                    fontWeight="600"
                    fontSize="md"
                    rows={2}
                    resize="none"
                    colorScheme="purple"
                    _focus={{
                      borderColor: "purple.700",
                      boxShadow: "0 0 0 1px purple.700"
                    }}
                  />
                </FormControl>
              </Box>
            </Collapse>
          </Box>
          {currentNode ? (
            <Stack spacing={5}>

                {/* Metric Selection Section */}
                <Box
                  borderWidth="2px"
                  borderRadius="md"
                  borderColor="purple.700"
                  boxShadow="md"
                  _hover={isMetricsExpanded ? { bg: "transparent" } : { bg: "purple.50" }}
                >
                  {/* Header - Always Visible */}
                  <HStack 
                    spacing={2} 
                    cursor="pointer" 
                    onClick={toggleMetricsExpansion}
                    p={3}
                    borderRadius="md"
                    justifyContent="space-between"
                  >
                    <HStack spacing={2}>
                    <Icon as={MdBarChart} boxSize={5} color="purple.700" />
                    <Text fontWeight="700" fontSize="lg" mb={0} color="purple.700">
                      Select Metrics
                    </Text>
                    {/* Show count when collapsed */}
                    {!isMetricsExpanded && selectedMetrics?.length > 0 && (
                      <Badge bg="purple.700" ml={2} size="lg" color="white">
                        {selectedMetrics.length} {selectedMetrics.length > 1 ? "metrics" : "metric"}
                      </Badge>
                    )}
                  </HStack>
                    {/* Show minimize button when expanded */}
                    {isMetricsExpanded && (
                      <IconButton
                        aria-label="Minimize section"
                        icon={<Icon as={FaRegWindowMinimize} boxSize={4} color="purple.700" />}
                        size="sm"
                        variant="ghost"
                        color="purple.700"
                        _hover={{ bg: "purple.100" }}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering parent onClick
                          toggleMetricsExpansion();
                        }}
                      />
                    )}
                  </HStack>
                        {/* Help text when collapsed */}
                    {!isMetricsExpanded && (
                      <Box 
                        px={3} 
                        pb={3} 
                        cursor="pointer" 
                        onClick={toggleMetricsExpansion}
                        _hover={{ bg: "purple.50" }}
                        >
                        <Text fontSize="sm" color="gray.600" mb={0}>
                         Choose metrics and KPIs Anko can analyze
                        </Text>
                      </Box>
                    )}
                  {/* Collapsible Content */}
                  <Collapse in={isMetricsExpanded} animateOpacity>
                    <Box p={3} pt={0}>
                  
                  {/* Metric Dropdown */}
                  <FormControl>
                    {/* <FormLabel>Select Metric</FormLabel> */}
                    <Menu closeOnSelect={true}>
                      <MenuButton
                        as={Button}
                        rightIcon={<ChevronDownIcon />}
                        w="100%"
                        textAlign="left"
                        variant="outline"
                        colorScheme="purple"
                        isDisabled={availableMetrics.length === 0}
                      >
                        {selectedMetric
                          ? selectedMetric.header?.name || "Unnamed Metric"
                          : availableMetrics.length === 0
                          ? "No metrics left"
                          : "Select a metric"}
                      </MenuButton>
                      <Portal>
                        <MenuList maxH="200px" overflowY="auto" zIndex={1500}>
                          {availableMetrics.map((metric, index) => (
                            <MenuItem
                              key={index}
                              onClick={() => handleSelectMetric(metric)}
                              _hover={{ bg: "purple.50" }}
                              isDisabled={isRecommendedMetric(metric.header?.id || "")}
                            >
                              <Flex alignItems="center" width="100%">
                                {metric.header?.name || "Unnamed Metric"}
                              </Flex>
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Portal>
                    </Menu>
                  </FormControl>

                  {/* Display Recommended Metrics */}
                  {recommendedMetrics.length > 0 && (
                    <Box mt={3} mb={2} borderWidth="1px" borderRadius="md" p={2} bg="gray.50">
                      <Text fontWeight="medium" fontSize="sm" mb={1}>
                        Any one of the following metrics from Parent Recommendations will be picked:
                      </Text>
                      <Stack spacing={1}>
                        {recommendedMetrics.map((metric, index) => (
                          <Flex
                            key={index}
                            borderWidth="1px"
                            borderRadius="md"
                            p={1}
                            alignItems="center"
                            backgroundColor="white"
                          >
                            <Box overflow="hidden" textOverflow="ellipsis">
                              <Text fontSize="sm" mb={0} fontWeight="medium">
                                {metric.header?.name || "Unnamed Metric"}
                              </Text>
                            </Box>
                            <Tag size="sm" colorScheme="green" ml="auto">
                              Add from parent
                            </Tag>
                          </Flex>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Display Selected Metrics */}
                  {selectedMetrics?.length > 0 && (
                    <Box mt={3}>
                      {/* <Text fontWeight="medium" mb={1}>
                        Selected Metrics:
                      </Text> */}
                       <Flex gap={3} flexWrap="wrap">
                        {selectedMetrics.map((metric) => (
                          <Flex
                            key={metric.header?.id}
                            borderWidth="1px"
                            borderRadius="md"
                            px={1}
                            justifyContent="space-between"
                            alignItems="center"
                            backgroundColor="purple.700"
                            borderColor="purple.700"
                            _hover={{ bg: "purple.600" }}
                          >
                          <Text 
                              fontWeight="bold" 
                              fontSize="sm" 
                              mb={0} 
                              ml={1} 
                              color="white"
                              noOfLines={1}
                              flex="1"
                            >
                              {metric.header?.name}
                            </Text>
                            <IconButton
                              aria-label="Remove metric"
                              icon={<CloseIcon boxSize={2} _hover={{boxSize:4}} />}
                              size="xs"
                              onClick={() => handleRemoveMetric(metric.header?.id || "")}
                              variant="ghost"
                              colorScheme="red"
                              color="red.50"
                              _hover={{ bg: "transparent"}}
                            />
                          </Flex>
                        ))}
                      </Flex>
                      
                      {/* Metrics Percolation Button */}
                      <Button
                        mt={3}
                        size="sm"
                        variant="outline"
                        colorScheme="purple"
                        leftIcon={<Icon as={TbFilterBolt} boxSize={5} color="purple.700" />}
                        onClick={() => handlePropagateMetricsClick(currentNode?.id || "")}
                        fontWeight="600"
                        borderColor="purple.700"
                        color="purple.700"
                        _hover={{ bg: "purple.50", borderColor: "purple.600" }}
                        width="100%"
                      >
                        Copy All Metrics Across Plan ({selectedMetrics.length})
                      </Button>
                    </Box>
                  )}
                  </Box>
                  </Collapse>

                </Box>

                
                {/* Add Filters Section */}
                <Box
                  borderWidth="2px"
                  borderRadius="md"
                  borderColor="purple.700"
                  boxShadow="md"
                  _hover={isFiltersExpanded ? { bg: "transparent" } : { bg: "purple.50" }}
                >
                  {/* Header - Always Visible */}
                  <HStack 
                    spacing={2} 
                    cursor="pointer" 
                    onClick={toggleFiltersExpansion}
                    p={3}
                    borderRadius="md"
                    justifyContent="space-between"
                  >
                  <HStack spacing={2}>
                    <Icon as={FaFilter} boxSize={5} color="purple.700" />
                    <Text fontWeight="700" fontSize="lg" mb={0} color="purple.700">
                      Add Filters
                    </Text>
                    {/* Show count when collapsed */}
                    {!isFiltersExpanded && filters?.length > 0 && (
                      <Badge bg="purple.700" ml={2} size="lg" color="white">
                        {filters.length} filters
                      </Badge>
                    )}
                    </HStack>
                    {/* Show minimize button when expanded */}
                    {isFiltersExpanded && (
                      <IconButton
                        aria-label="Minimize section"
                        icon={<Icon as={FaRegWindowMinimize} boxSize={4} color="purple.700" />}
                        size="sm"
                        variant="ghost"
                        color="purple.700"
                        _hover={{ bg: "purple.100" }}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering parent onClick
                          toggleFiltersExpansion();
                        }}
                      />
                    )}
                  </HStack>
                  {/* Help text when collapsed */}
                  {!isFiltersExpanded && (
                    <Box 
                      px={3} 
                      pb={3} 
                      cursor="pointer" 
                      onClick={toggleFiltersExpansion}
                      _hover={{ bg: "purple.50" }}
                    >
                      <Text fontSize="sm" color="gray.600" mb={0}>
                      Define filters for Anko to focus on data segments
                      </Text>
                    </Box>
                  )}
  
                  <Collapse in={isFiltersExpanded} animateOpacity>
                  <Box p={3} pt={0}>
                  <Filters
                    currentNodeId={currentNode.id}
                    filters={filters}
                    setFilters={setFilters}
                    nonBenchmarkEdges={currentNode?.non_benchmarks}
                    temporalColumns={currentNode?.temporal_columns}
                    recommendedFilters={recommendedFilters}
                  />
                  </Box>
                  </Collapse>
                </Box>

                {/* Split By Selection Section */}
                <Box
                  borderWidth="2px"
                  borderRadius="md"
                  borderColor="purple.700"
                  boxShadow="md"
                  _hover={isColumnsExpanded ? { bg: "transparent" } : { bg: "purple.50" }}
                >
                  {/* Header - Always Visible */}
                  <HStack 
                    spacing={2} 
                    cursor="pointer" 
                    onClick={toggleColumnsExpansion}
                    borderRadius="md"
                    p={3}
                    justifyContent="space-between"
                  >
                     <HStack spacing={2}>
                      <Icon as={AiFillPieChart} boxSize={6} color="purple.700" />
                      <Text fontWeight="700" fontSize="lg" mb={0} color="purple.700">
                        Split by Attribute
                      </Text>
                      {/* Show count when collapsed */}
                      {!isColumnsExpanded && selectedColumns?.length > 0 && (
                        <Badge colorScheme="purple" ml={2} bg="purple.700" color="white">
                         {selectedColumns.length} {selectedColumns.length > 1 ? " Attributes" : " Attribute"}
                        </Badge>
                      )}
                    </HStack>
                   {/* Show minimize button when expanded */}
                    {isColumnsExpanded && (
                      <IconButton
                        aria-label="Minimize section"
                        icon={<Icon as={FaRegWindowMinimize} boxSize={4} color="purple.700" />}
                        size="sm"
                        variant="ghost"
                        color="purple.700"
                        _hover={{ bg: "purple.100" }}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering parent onClick
                          toggleColumnsExpansion();
                        }}
                      />
                    )}
                  </HStack>
                  {/* Help text when collapsed */}
                  {!isColumnsExpanded && (
                    <Box 
                      px={3} 
                      pb={3} 
                      cursor="pointer" 
                      onClick={toggleColumnsExpansion}
                      _hover={{ bg: "purple.50" }}
                    >
                      <Text fontSize="sm" color="gray.600" mb={0}>
                      Select business dimensions for Anko to group data
                      </Text>
                    </Box>
                  )}
                  <Collapse in={isColumnsExpanded} animateOpacity>
                  <Box p={2} pt={0}>
                  {/* Column Dropdown */}
                  <FormControl>
                    {/* <FormLabel>Select Column</FormLabel> */}
                    <Menu closeOnSelect={true}>
                      <MenuButton
                        as={Button}
                        rightIcon={<ChevronDownIcon />}
                        w="100%"
                        textAlign="left"
                        variant="outline"
                        colorScheme="purple"
                        isDisabled={nonMetricColumns.length === 0}
                      >
                        {selectedColumn
                          ? selectedColumn.name || "Unnamed Column"
                          : nonMetricColumns.length === 0
                          ? "No columns available"
                          : "Select Attribute"}
                      </MenuButton>
                      <Portal>
                        <MenuList maxH="200px" overflowY="auto" zIndex={1500}>
                          {nonMetricColumns?.map((column, index) => (
                            <MenuItem
                              key={index}
                              onClick={() => handleSelectColumn(column)}
                              _hover={{ bg: "purple.50" }}
                              isDisabled={
                                selectedColumns.some(c => c.column_id === column.column_id) ||
                                isRecommendedColumn(column.column_id || "")
                              }
                              fontWeight="600"
                            >
                              {column.kg_column_header?.name}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Portal>
                    </Menu>
                  </FormControl>
                  </Box>
                  


                  {/* Display Recommended Columns */}
                  {recommendedColumns.length > 0 && (
                    <Box mt={3} mb={2} borderWidth="1px" borderRadius="md" p={2} bg="gray.50">
                      <Text fontWeight="medium" fontSize="sm" mb={1}>
                        Any one of the following columns from Parent Recommendations will be picked:
                      </Text>
                      <Stack spacing={1}>
                        {recommendedColumns?.map((column, index) => (
                          <Flex
                            key={index}
                            borderWidth="1px"
                            borderRadius="md"
                            p={1}
                            alignItems="center"
                            backgroundColor="white"
                          >
                            <Box overflow="hidden" textOverflow="ellipsis">
                              <Text fontSize="sm" mb={0} fontWeight="medium">
                                {column.kg_column_header?.name || "Unnamed Column"}
                              </Text>
                            </Box>
                            <Tag size="sm" colorScheme="green" ml="auto">
                              Add from parent
                            </Tag>
                          </Flex>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Display Selected Columns */}
                  {selectedColumns?.length > 0 && (
                    <Box p={2}>
                        {/* <Text fontWeight="medium" mb={1}>
                          Selected Columns:
                        </Text> */}
                      <Stack spacing={2}>
                        {selectedColumns?.map((column) => (
                          <Box
                            key={column.column_id}
                            borderWidth="1px"
                            borderRadius="md"
                            p={1}
                            backgroundColor="gray.50"
                            borderColor="purple.700"
                          >
                            <Flex justifyContent="space-between" alignItems="center">
                              <Text fontWeight="bold" fontSize="sm" mb={0} ml={1}>
                               Split by {column?.kg_column_header?.name}
                              </Text>
                              <IconButton
                                aria-label="Remove column"
                                icon={<Icon as={CloseIcon} boxSize={3} _hover={{boxSize:4}} />}
                                size="xs"
                                onClick={() => handleRemoveColumn(column?.column_id || "")}
                                variant="ghost"
                                colorScheme="red"
                              />
                            </Flex>

                            {/* Inline time bucket selection for temporal columns */}
                            {column?.kg_column_header?.column_type === ColumnType.TEMPORAL && (
                              <Box>
                                <Text fontSize="xs" color="gray.600" mb={0} ml={1} fontWeight="700">
                                  Time Buckets
                                </Text>
                                  <Flex gap={1} flexWrap="wrap" p={1}>
                                   {Object?.entries(TimeBucket)
                                     ?.filter(([key, value]) => typeof value === "number" && value !== TimeBucket.UNRECOGNIZED)
                                     .map(([key, value]) => (
                                       <Button
                                         key={key}
                                         size="xs"
                                         variant={column.time_bucket === value ? "solid" : "outline"}
                                         colorScheme="purple"
                                         onClick={() => handleTimeBucketChange(column?.column_id || "", value as TimeBucket)}
                                         bg={column.time_bucket === value ? "purple.700" : "white"}
                                       >
                                         {formatToSentenceCase(key)}
                                       </Button>
                                     ))}
                                 </Flex>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  </Collapse>

                </Box>


                {/* Add Benchmarks along with calculations */}
                <Box
                  borderWidth="2px"
                  borderRadius="md"
                  borderColor="purple.700"
                  boxShadow="md"
                  _hover={isBenchmarksExpanded ? { bg: "transparent" } : { bg: "purple.50" }}
                >
                {/* Header - Always Visible */}
                <HStack 
                    spacing={2} 
                    cursor="pointer" 
                    onClick={toggleBenchmarksExpansion}
                    p={3}
                    borderRadius="md"
                    justifyContent="space-between"
                  >
                    <HStack spacing={2}>
                    <Icon as={TbMathFunction} boxSize={6} color="purple.700" />
                    <Text fontWeight="700" fontSize="lg" mb={0} color="purple.700">
                      Define Benchmark
                    </Text>
                    {/* Show count when collapsed */}
                    {!isBenchmarksExpanded && benchmarkColumnHashes?.length > 0 && (
                      <Badge colorScheme="purple" ml={2} bg="purple.700" color="white">
                        {(() => {
                          // Count unique benchmarks from benchmarkColumnHashes
                          const uniqueBenchmarks = new Set(benchmarkColumnHashes.map(hash => hash.benchmark_id));
                          return `${uniqueBenchmarks.size} ${uniqueBenchmarks.size > 1 ? "analyses" : "analysis"}`;
                        })()}
                      </Badge>
                    )}
                    </HStack>
                    {/* Show minimize button when expanded */}
                    {isBenchmarksExpanded && (
                      <IconButton
                        aria-label="Minimize section"
                        icon={<Icon as={FaRegWindowMinimize} boxSize={4} color="purple.700" />}
                        size="sm"
                        variant="ghost"
                        color="purple.700"
                        _hover={{ bg: "purple.100" }}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering parent onClick
                          toggleBenchmarksExpansion();
                        }}
                      />
                    )}
                  </HStack>

                  {/* Help text when collapsed */}

                  {!isBenchmarksExpanded && (
                    <Box 
                      px={3} 
                      pb={3} 
                      cursor="pointer" 
                      onClick={toggleBenchmarksExpansion}
                      _hover={{ bg: "purple.50" }}
                    >
                      <Text fontSize="sm" color="gray.600" mb={0}>
                        Define analysis columns and benchmarks for Anko
                      </Text>
                    </Box>
                  )}
                  <Collapse in={isBenchmarksExpanded} animateOpacity>
                  <Box p={3} pt={0}>
                  <Benchmarks
                    currentNode={currentNode}
                    selectedBenchmarks={selectedBenchmarks}
                    setSelectedBenchmarks={setSelectedBenchmarks}
                    benchmarkColumnHashes={benchmarkColumnHashes}
                    setBenchmarkColumnHashes={setBenchmarkColumnHashes}
                    selectedColumns={selectedColumns}
                  />
                  </Box>
                  </Collapse>
                </Box>

                {/* Analysis Section */}
                <Box
                  borderWidth="2px"
                  borderRadius="md"
                  borderColor="purple.700"
                  boxShadow="md"
                  _hover={isAnalysisExpanded ? { bg: "transparent" } : { bg: "purple.50" }}
                >
                  {/* Header - Always Visible */}
                  <HStack 
                    spacing={2} 
                    cursor="pointer" 
                    onClick={toggleAnalysisExpansion}
                    p={3}
                    borderRadius="md"
                    justifyContent="space-between"
                  >
                    <HStack spacing={2}>
                      <Icon as={TbMathFunction} boxSize={6} color="purple.700" />
                      <Text fontWeight="700" fontSize="lg" mb={0} color="purple.700">
                        Define Analysis
                      </Text>
                      {/* Show count when collapsed */}
                      {!isAnalysisExpanded && analysisColumns?.length > 0 && (
                        <Badge colorScheme="purple" ml={2} bg="purple.700" color="white">
                          {analysisColumns.length} {analysisColumns.length > 1 ? "analyses" : "analysis"}
                        </Badge>
                      )}
                    </HStack>
                    {/* Show minimize button when expanded */}
                    {isAnalysisExpanded && (
                      <IconButton
                        aria-label="Minimize section"
                        icon={<Icon as={FaRegWindowMinimize} boxSize={4} color="purple.700" />}
                        size="sm"
                        variant="ghost"
                        color="purple.700"
                        _hover={{ bg: "purple.100" }}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering parent onClick
                          toggleAnalysisExpansion();
                        }}
                      />
                    )}
                  </HStack>

                  {/* Help text when collapsed */}
                  {!isAnalysisExpanded && (
                    <Box 
                      px={3} 
                      pb={3} 
                      cursor="pointer" 
                      onClick={toggleAnalysisExpansion}
                      _hover={{ bg: "purple.50" }}
                    >
                      <Text fontSize="sm" color="gray.600" mb={0}>
                        Define analysis calculations for selected metrics and measures
                      </Text>
                    </Box>
                  )}
                  <Collapse in={isAnalysisExpanded} animateOpacity>
                    <Box p={3} pt={0}>
                      <Analysis
                        currentNode={currentNode}
                        analysisColumns={analysisColumns}
                        setAnalysisColumns={setAnalysisColumns}
                        selectedColumns={selectedColumns}
                        selectedMetrics={selectedMetrics}
                        selectedBenchmarks={selectedBenchmarks}
                        benchmarkColumnHashProto={benchmarkColumnHashes}
                      />
                    </Box>
                  </Collapse>
                </Box>

                {/* Recommendation Section */}
                {/* <Box
                  borderWidth="2px"
                  borderRadius="md"
                  mb={4}
                  p={4}
                  borderColor="blue.300"
                  boxShadow="md"
                >
                  <Text fontWeight="bold" fontSize="lg" mb={3} color="blue.600">
                    Add Recommendations
                  </Text>
                  <KGRecommendations
                    currentNode={currentNode}
                    addChildToRecommendation={handleAddChildNode}
                   />
                </Box> */}

                {/* Recommended Filters Section - Show only when Get Data is clicked and filters exist */}
                {isLoadingFilterValues ? (
                  <Center height="200px">
                  <VStack spacing={3}>
                    <Spinner size="lg" color="purple.500" colorScheme="purple" />
                    <Text color="gray.600">Loading filter values...</Text>
                  </VStack>
                </Center>
                ) : (
                  <>
                  {showRecommendedFilters && recommendedFiltersToApply.length > 0 && (
                    <Box
                      borderWidth="2px"
                      borderRadius="md"
                      mb={4}
                      p={4}
                      borderColor="purple.700"
                      boxShadow="md"
                      bg="purple.50"
                    >
                      <Text fontWeight="bold" fontSize="lg" mb={3} color="purple.700">
                        Specify Missing Filters
                      </Text>
                      <Text fontSize="sm" color="gray.600" mb={3}>
                       The below filters are missing. Please specify the values before loading data.
                      </Text>
  
                      <Stack spacing={4}>
                        {recommendedFiltersToApply.map((filter, index) => {
                          const columnId = filter.kg_column_header?.id;
                          const selected = columnId ? selectedRecommendedFilters.get(columnId) || [] : [];
  
                          return (
                            <Box
                              key={index}
                              borderWidth="1px"
                              borderRadius="lg"
                              p={3}
                              bg="white"
                              boxShadow="sm"
                            >
                              <VStack align="start" spacing={3}>
                                {/* Filter Header */}
                                <Text fontSize="md" fontWeight="semibold" mb={0}>
                                  {filter?.kg_column_header?.name || "Unknown Column"}
                                </Text>
  
                                {/* Selected Values Display */}
                                {selected.length > 0 && (
                                  <Box width="100%">
                                    <HStack spacing={2} wrap="wrap">
                                      {selected.map((value, idx) => (
                                        <Tag
                                          key={idx}
                                          size="md"
                                          borderRadius="md"
                                          variant="solid"
                                          colorScheme="purple"
                                          bg="purple.700"
                                          color="white"
                                        >
                                          <TagLabel>{value}</TagLabel>
                                          <TagCloseButton
                                            onClick={() => {
                                              if (columnId) {
                                                const newSelected = selected.filter(v => v !== value);
                                                const newMap = new Map(selectedRecommendedFilters);
                                                newMap.set(columnId, newSelected);
                                                setSelectedRecommendedFilters(newMap);
                                              }
                                            }}
                                          />
                                        </Tag>
                                      ))}
                                    </HStack>
                                  </Box>
                                )}
  
                                {/* Values Selection */}
                                <FormControl width="100%">
                                  <ColumnValues
                                    contextValues={columnId ? recommendedFilterValues.get(columnId) || [] : []}
                                    selectedOptions={selected}
                                    setSelectedOptions={(newValues: string[]) => {
                                      if (columnId) {
                                        const newMap = new Map(selectedRecommendedFilters);
                                        newMap.set(columnId, newValues);
                                        setSelectedRecommendedFilters(newMap);
                                      }
                                    }}
                                  />
                                </FormControl>
                              </VStack>
                            </Box>
                          );
                        })}
                      </Stack>
  
                      {/* Action Buttons */}
                      <HStack spacing={3} mt={4} justify="flex-end">
                        {/* <Button
                          variant="ghost"
                          onClick={handleSkipRecommendedFilters}
                          size="sm"
                        >
                          Skip & Get Data
                        </Button> */}
                        <Button
                          colorScheme="purple"
                          onClick={handleApplyRecommendedFilters}
                          size="sm"
                          isDisabled={Array.from(selectedRecommendedFilters.values()).every(
                            (values) => values.length === 0
                          )}
                          bg="purple.700"
                          _hover={{ bg: "purple.600" }}
                          leftIcon={<Icon as={MdOutlineQueryStats} boxSize={5} color="white" />}
                          fontWeight="600"
                          fontSize="md"
                          flex="1"
                        >
                          Apply Filters & Load
                        </Button>
                      </HStack>
                    </Box>
                  )}
                  </>
                )}

                {/* Propagation Modal */}
                <PropagationModal
                  isOpen={propagationModal.isOpen}
                  onClose={handleClosePropagation}
                  item={propagationModal.item}
                  availableNodes={propagationModal.availableNodes}
                  shouldOverwrite={propagationModal.shouldOverwrite}
                  onToggleOverwrite={handlePropagationOverwriteToggle}
                  onToggleNode={handlePropagationNodeToggle}
                  onConfirm={handleConfirmPropagation}
                />
            </Stack>
          ) : (
            <Text>No node selected</Text>
          )}
        </DrawerBody>
        <DrawerFooter>
          {/* <Button variant="outline" mr={3} onClick={configurator.onClose}>
            Cancel
          </Button> */}
          <HStack width="100%" spacing={1}>
            <IconButton
              aria-label="Close configurator"
              icon={<Icon as={TbLayoutSidebarRightCollapseFilled} boxSize={9} color="gray.500" />}
              size="xs"
              variant="ghost"
              color="gray.500"
              _hover={{ bg: "gray.100", color: "gray.700" }}
              // onClick={configurator.onClose}
              onClick={()=>{
                configurator.onClose();
                setSelectedNodeId(null);
              }}
              flexShrink={0}
              ml={-3}
             />     
            <Button 
              colorScheme="purple"
              bg="purple.700"
              onClick={handleGetData}
              isLoading={isGettingData}
              loadingText="Getting Data..."
              isDisabled={isGettingData || !currentNode || showRecommendedFilters}
              leftIcon={<Icon as={MdOutlineQueryStats} boxSize={7} color="white" />}
              fontWeight="700"
              fontSize="lg"
              flex="1"
              _hover={{ bg: "purple.600" }}
            >
              LOAD DATA
            </Button>
          </HStack>
          {/* <Button colorScheme="blue" onClick={() => handleSave()}>
            Save
          </Button> */}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default NodeConfigurator;